import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Download, X, Users, Eye, EyeOff, ZoomIn, ZoomOut, Move, Play, Instagram, Camera, Video, Twitter, MessageCircle, Music, ExternalLink } from 'lucide-react';
import { stalkerAPI, getStaticFileUrl, getProxyUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { PLATFORM_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

const Medias: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [medias, setMedias] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [showHidden, setShowHidden] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [lightboxImage, setLightboxImage] = useState<string | null>(null);
	const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image');
	const [lightboxMedia, setLightboxMedia] = useState<any>(null);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const observerTarget = useRef<HTMLDivElement>(null);
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/medias', 'Stalker', 'Medias');
			hasLoggedPageView.current = true;
		}
	}, []);

	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
					loadMore();
				}
			},
			{ threshold: 0.1 }
		);

		const currentTarget = observerTarget.current;
		if (currentTarget && hasMore) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMore, loadingMore, loading]);

	// Lock body scroll when lightbox is open
	useEffect(() => {
		if (lightboxImage) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [lightboxImage]);

	const formatDate = (dateString: string | undefined): string => {
		if (!dateString) return '';
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	};

	const loadData = async () => {
		try {
			setLoading(true);
			const response = await stalkerAPI.medias.getList({ limit: 30, offset: 0 });
			setMedias(response.data.data || []);
			const total = response.data.pagination?.total || 0;
			setTotalCount(total);
			setHasMore(response.data.pagination?.hasMore || false);
			setPage(1);
		} catch (error) {
			console.error('Error loading medias:', error);
		} finally {
			setLoading(false);
		}
	};

	const loadMore = async () => {
		if (loadingMore || !hasMore) return;

		try {
			setLoadingMore(true);
			const nextPage = page + 1;
			const offset = (nextPage - 1) * 30;
			const response = await stalkerAPI.medias.getList({ limit: 30, offset });
			const newMedias = response.data.data || [];
			const pagination = response.data.pagination || {};

			if (newMedias.length > 0) {
				setMedias(prev => [...prev, ...newMedias]);
				setPage(nextPage);
				const total = pagination.total || totalCount;
				setTotalCount(total);
				// Backend'den gelen hasMore değerini kullan
				setHasMore(pagination.hasMore === true);
			} else {
				setHasMore(false);
			}
		} catch (error) {
			console.error('Error loading more medias:', error);
			setHasMore(false);
		} finally {
			setLoadingMore(false);
		}
	};

	const filteredMedias = medias.filter(media => {
		const matchesSearch = globalSearchTerm === '' ||
			media.sm_fileName?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			media.sm_description?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			media.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			media.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || media.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const handleDownload = async (media: any) => {
		const mediaUrl = media.sm_filePath
			? getStaticFileUrl(media.sm_filePath)
			: (media.sm_originalUrl ? getProxyUrl(media.sm_originalUrl) : null);

		if (mediaUrl) {
			try {
				const response = await fetch(mediaUrl);
				const blob = await response.blob();
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = media.sm_fileName || 'media';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			} catch (error) {
				console.error('Download error:', error);
			}
		}
	};

	const handleLightboxOpen = (mediaUrl: string, mediaType: 'image' | 'video', media?: any) => {
		setLightboxImage(mediaUrl);
		setLightboxType(mediaType);
		setLightboxMedia(media || null);
		setZoomLevel(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleLightboxClose = () => {
		setLightboxImage(null);
		setLightboxMedia(null);
		setZoomLevel(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleZoom = (delta: number) => {
		setZoomLevel(prev => Math.max(0.5, Math.min(5, prev + delta)));
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (zoomLevel > 1) {
			setIsDragging(true);
			setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && zoomLevel > 1) {
			setImagePosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleWheel = (e: React.WheelEvent) => {
		if (lightboxImage) {
			e.preventDefault();
			const delta = e.deltaY > 0 ? -0.1 : 0.1;
			handleZoom(delta);
		}
	};

	if (loading && page === 1) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_MEDIA_RECORDS... ]</span>
				</div>
			</div>
		);
	}

	const getPlatformIcon = (platform: string, className = "w-3 h-3") => {
		switch (platform.toLowerCase()) {
			case 'instagram': return <Instagram className={`${className} ${isDark ? 'text-pink-500' : 'text-pink-600'}`} />;
			case 'vsco': return <Camera className={`${className} ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`} />;
			case 'tiktok': return <Video className={`${className} ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />;
			case 'x':
			case 'twitter': return <Twitter className={`${className} ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />;
			case 'snapchat': return <MessageCircle className={`${className} ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />;
			case 'spotify': return <Music className={`${className} ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />;
			default: return <ExternalLink className={`${className} ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />;
		}
	};

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className="text-sm font-mono font-bold tracking-widest uppercase flex items-center gap-2">
						<div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.8)]"></div>
						<span className={isDark ? 'text-neutral-200' : 'text-neutral-800'}>SYS: INTERCEPTED_MEDIA</span>
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${filteredMedias.length} / ${totalCount}` : `TOTAL_RECORDS: ${totalCount}`}
					</p>
				</div>
				<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap gap-2">
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors ${
							isDark 
								? showHidden ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-white' 
								: showHidden ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-neutral-50 border-neutral-300 text-neutral-600 hover:text-black'
						}`}
						title={showHidden ? 'HIDE_SECURE_RECORDS' : 'SHOW_SECURE_RECORDS'}
					>
						{showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
						<span className="hidden sm:inline">{showHidden ? 'SECURE_VISIBLE' : 'SECURE_HIDDEN'}</span>
					</button>
				</div>
			</div>

			{filteredMedias.length > 0 ? (
				<>
					<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
						{filteredMedias.map((media) => {
							const mediaUrl = media.sm_filePath
								? getStaticFileUrl(media.sm_filePath)
								: (media.sm_originalUrl ? getProxyUrl(media.sm_originalUrl) : null);

							return (
								<div key={media.sm_id} className={`p-2 border relative group transition-colors flex flex-col justify-between ${isDark ? 'bg-[#111] hover:bg-[#1a1a1a] border-[#222] hover:border-[#444]' : 'bg-white hover:bg-neutral-50 border-neutral-200'}`}>
									
                                    {/* Info Header */}
                                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-transparent">
                                        <div className="flex items-center space-x-2.5 overflow-hidden">
                                            {media.person_avatar ? (
                                                <img
                                                    src={getStaticFileUrl(media.person_avatar) || ''}
                                                    alt="AVT"
                                                    className={`w-12 h-12 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/stalker/persons/${media.person_id}`); }}
                                                />
                                            ) : (
                                                <div 
                                                    className={`w-12 h-12 flex-shrink-0 flex items-center justify-center font-mono text-sm border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/stalker/persons/${media.person_id}`); }}
                                                >
                                                    <Users className="w-5 h-5" />
                                                </div>
                                            )}
                                            <div className="flex flex-col min-w-0">
                                                <button
                                                    onClick={() => navigate(`/stalker/socials/${media.sm_platform}/${media.sm_socialId}`)}
                                                    className={`text-[12px] font-mono font-bold tracking-wider uppercase hover:underline truncate ${isDark ? 'text-neutral-300' : 'text-neutral-800'}`}
													style={{textAlign: "left"}}
                                                >
                                                    @{media.platform_username || media.person_name}
                                                </button>
                                                <span className={`text-[10px] font-mono tracking-widest uppercase truncate opacity-70 ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
                                                    // {PLATFORM_MAP[media.sm_platform] || media.sm_platform}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={`p-2 border ${isDark ? 'border-[#333] bg-black/40' : 'border-neutral-200 bg-neutral-50'} flex-shrink-0`}>
                                            {getPlatformIcon(media.sm_platform, "w-4 h-4")}
                                        </div>
                                    </div>

									{/* Media */}
									{mediaUrl && (
										<div className={`relative mb-2 w-full aspect-square border overflow-hidden ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-200 bg-neutral-100'}`}>
											{media.sm_mediaType === 'video' ? (
												<div className="w-full h-full cursor-pointer hover:opacity-80 transition-opacity relative" onClick={(e) => {
													e.stopPropagation();
													handleLightboxOpen(mediaUrl, 'video', media);
												}}>
													<video
														src={mediaUrl}
														className="w-full h-full object-cover"
														preload="metadata"
													/>
													<div className="absolute inset-0 flex items-center justify-center bg-black/40">
														<Play className={`w-6 h-6 ${isDark ? 'text-neutral-300' : 'text-white'}`} fill="currentColor" />
													</div>
												</div>
											) : (
												<img
													src={mediaUrl}
													alt="Media"
													className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
													onClick={() => handleLightboxOpen(mediaUrl, 'image', media)}
												/>
											)}
											{/* Download button */}
											<div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
												<button
													onClick={(e) => {
														e.stopPropagation();
														handleDownload(media);
													}}
													className={`p-1.5 border transition-all ${isDark ? 'bg-black/80 border-[#333] hover:text-emerald-400 text-neutral-400' : 'bg-white/90 border-neutral-300 hover:text-emerald-600 text-neutral-500'}`}
													title="DOWNLOAD"
												>
													<Download className="w-3.5 h-3.5" />
												</button>
											</div>
										</div>
									)}

									{/* Media Info */}
									<div className="mt-auto space-y-1">
										{media.sm_downloadedAt && (
											<p className={`text-[8px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
												D/L: {formatDate(media.sm_downloadedAt)}
											</p>
										)}
										{media.sm_description && (
											<button
												onClick={(e) => {
													e.stopPropagation();
													if (media.person_id) {
														navigate(`/stalker/persons/${media.person_id}?tab=medias`);
													}
												}}
												className={`text-[9px] font-mono leading-tight tracking-wider line-clamp-2 text-left w-full hover:underline ${isDark ? 'text-neutral-400 hover:text-neutral-300' : 'text-neutral-600 hover:text-neutral-800'}`}
												title={media.sm_description}
											>
												{media.sm_description}
											</button>
										)}
									</div>
								</div>
							);
						})}
					</div>

					{/* Infinite scroll trigger */}
					{hasMore && (
						<div ref={observerTarget} className="flex justify-center py-4 min-h-[50px]">
							{loadingMore && (
								<div className="flex items-center space-x-2 font-mono text-[10px] uppercase tracking-widest text-emerald-500">
									<div className="w-1 h-3 bg-current animate-pulse" />
									<span>[ FETCHING_MORE... ]</span>
								</div>
							)}
						</div>
					)}
				</>
			) : (
				<div className={`border p-8 text-center font-mono text-xs uppercase tracking-widest ${isDark ? 'bg-[#111] border-[#222] text-neutral-600' : 'bg-white border-neutral-200 text-neutral-400'}`}>
					[ NO_RECORDS_FOUND ]
				</div>
			)}

			{/* Lightbox */}
			{lightboxImage && createPortal(
				<div
					className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[9999]"
					onClick={handleLightboxClose}
					onWheel={handleWheel}
				>
					{/* Download and Close Buttons */}
					<div className="absolute top-4 right-4 flex items-center space-x-3 z-10">
						<button
							onClick={async (e) => {
								e.stopPropagation();
								if (lightboxImage) {
									try {
										const response = await fetch(lightboxImage);
										const blob = await response.blob();
										const url = window.URL.createObjectURL(blob);
										const link = document.createElement('a');
										link.href = url;
										link.download = lightboxMedia?.sm_fileName || 'media';
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);
										window.URL.revokeObjectURL(url);
									} catch (error) {
										console.error('Download error:', error);
									}
								}
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="DOWNLOAD"
						>
							<Download className="w-5 h-5" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleLightboxClose();
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="CLOSE"
						>
							<X className="w-5 h-5" />
						</button>
					</div>

					{lightboxType === 'image' ? (
						<div
							className="relative max-w-[90vw] max-h-[90vh]"
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onMouseLeave={handleMouseUp}
						>
							<img
								src={lightboxImage}
								alt="Lightbox"
								className="max-w-full max-h-[90vh] object-contain border border-[#333]"
								style={{
									transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
									cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
								}}
								onClick={(e) => e.stopPropagation()}
							/>
							{zoomLevel > 1 && (
								<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-black/80 border border-[#333] text-emerald-500 font-mono text-[10px] px-4 py-2 uppercase tracking-wide">
									<ZoomOut className="w-3.5 h-3.5" />
									<span>{Math.round(zoomLevel * 100)}%</span>
									<ZoomIn className="w-3.5 h-3.5" />
									<Move className="w-3.5 h-3.5 ml-2" />
								</div>
							)}
						</div>
					) : (
						<video
							src={lightboxImage}
							controls
							className="max-w-[90vw] max-h-[90vh] border border-[#333]"
							onClick={(e) => e.stopPropagation()}
						/>
					)}
				</div>,
				document.body
			)}
		</div>
	);
};

export default Medias;

